import { useEffect, useRef, useState } from 'react';
import { LifeBuoy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ReportProblemDialog } from './ReportProblemDialog';
import { useMyTickets } from '@/hooks/useSupportTickets';

/**
 * Floating "Ayuda" button — bottom-right de cada pantalla.
 *
 * Comportamiento (simplificado a una sola acción):
 *   - Tap normal      → navega a /soporte (Centro de Soporte completo)
 *   - Long-press      → abre directamente el modal de "Nuevo reporte"
 *                       (atajo power-user para reportar sin pasar por
 *                        la página intermedia)
 *   - Badge numérico  → cantidad de tickets sin resolver (visual hint)
 *
 * Antes el FAB abría un mini popup con últimos 3 tickets + 2 botones,
 * lo cual duplicaba info que ya está en /soporte. Eliminado para
 * reducir confusión: una sola acción clara.
 */
export function SupportFab() {
  const navigate = useNavigate();
  const [reportOpen, setReportOpen] = useState(false);
  const { tickets } = useMyTickets();
  const openCount = tickets.filter(t => t.status !== 'resolved').length;

  // Ghost-after-idle behaviour, mirroring ConnectionIndicator. El pill
  // está visible 5s, luego se hace traslúcido para no competir con el
  // resto del contenido. Cualquier interacción lo despierta.
  const [ghost, setGhost] = useState(false);
  const ghostTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const wake = () => {
    if (ghostTimerRef.current) clearTimeout(ghostTimerRef.current);
    setGhost(false);
    ghostTimerRef.current = setTimeout(() => setGhost(true), 5000);
  };

  useEffect(() => {
    wake();
    return () => {
      if (ghostTimerRef.current) clearTimeout(ghostTimerRef.current);
    };
  }, []);

  // Despertar cuando aparezca un ticket nuevo abierto (badge cambia)
  useEffect(() => { wake(); }, [openCount]);

  return (
    <>
      <div
        className="fixed z-40"
        style={{
          right: 'max(env(safe-area-inset-right), 1rem)',
          bottom: 'calc(max(env(safe-area-inset-bottom), 1rem) + 64px)',
        }}
      >
        <button
          onClick={() => { wake(); navigate('/soporte'); }}
          onMouseEnter={wake}
          onTouchStart={wake}
          onFocus={wake}
          onContextMenu={(e) => {
            // Long-press / right-click → atajo a reporte directo
            e.preventDefault();
            wake();
            setReportOpen(true);
          }}
          className={`relative flex items-center gap-2 px-4 py-2.5 md:px-5 md:py-3 rounded-full bg-accent text-accent-foreground shadow-2xl hover:scale-105 active:scale-95 hover:opacity-100 focus:opacity-100 transition-all duration-500 font-black uppercase tracking-wider text-xs md:text-sm ${
            ghost ? 'opacity-40' : 'opacity-100'
          }`}
          aria-label="Ayuda y soporte"
          title="Ayuda (mantén pulsado para nuevo reporte rápido)"
        >
          <LifeBuoy className="w-4 h-4 md:w-5 md:h-5" />
          <span>Ayuda</span>
          {openCount > 0 && (
            <span className="min-w-[18px] h-[18px] rounded-full bg-background text-foreground text-[10px] font-black flex items-center justify-center px-1">
              {openCount}
            </span>
          )}
        </button>
      </div>

      <ReportProblemDialog open={reportOpen} onOpenChange={setReportOpen} />
    </>
  );
}
